import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { reportPlanLimit } from '../utils/planLimit';
import { identifyUser, resetUser, track } from '../utils/analytics';
import { markAnonymous, wasAnonymous, journeyProperties, clearJourney } from '../utils/funnelJourney';
import { attributionSetOnceProperties } from '../utils/attribution';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const API_URL = process.env.REACT_APP_API_URL;

  // Fetch current user on mount (checks if HTTP-only cookie exists)
  useEffect(() => {
    getCurrentUser(true);
  }, []);

  // Tie product-analytics events to the signed-in user (no-op until a provider is wired).
  const lastIdentifiedUserId = useRef(null);
  useEffect(() => {
    if (user?.id) {
      // The funnel's last step (docs/launch/ws-demo-funnel.md): the person who was here without
      // an account now has one. Recognised here rather than in the sign-up call itself, because
      // Google's path leaves and comes back, and both routes land on this effect.
      if (user.isDemo) {
        markAnonymous();
      } else if (wasAnonymous()) {
        track('signup_from_demo', journeyProperties());
        clearJourney();
      }
      // Demo users are ephemeral and have no real identity, don't ship their PII to analytics.
      const traits = user.provider === 'demo'
        ? { provider: 'demo', demo: true }
        : { email: user.email, name: user.name, provider: user.provider };
      // Attribution goes in as $set_once (see utils/attribution.js): the campaign that brought
      // this person in the first time is what we want on their profile, not whichever session
      // happened to be running the last time identify() fired.
      identifyUser(user.id, traits, attributionSetOnceProperties());
      lastIdentifiedUserId.current = user.id;
    } else if (lastIdentifiedUserId.current) {
      // Reset only on a real sign-out transition, NOT on the initial null before /api/auth/me
      // resolves, which would otherwise churn the anonymous id/session on every page load.
      resetUser();
      lastIdentifiedUserId.current = null;
    }
  }, [user]);

  // Get current user from backend (cookies sent automatically)
  // setLoading: only set isLoading to false on initial mount, not on refresh
  const getCurrentUser = async (setLoading = false) => {
    try {
      let response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include' // Send cookies with request
      });

      // Access token may have expired, try a silent refresh before giving up
      if (response.status === 401) {
        const refreshResponse = await fetch(`${API_URL}/api/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        if (refreshResponse.ok) {
          response = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' });
        }
      }

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
        return userData;
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Failed to get current user:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      if (setLoading) {
        setIsLoading(false);
      }
    }
    return null;
  };

  // Login with Google - redirects to backend OAuth endpoint
  const loginWithGoogle = () => {
    window.location.href = `${API_URL}/oauth2/authorization/google`;
  };

  const register = async (email, fullName, password) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, fullName, password })
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setIsAuthenticated(true);
        track('signed_in', { provider: data.user?.provider || 'credentials', method: 'register', isNewUser: true });
        track('signup_completed', { method: 'credentials' });
        return { success: true };
      }
      if (response.status === 409) return { success: false, error: 'An account with this email already exists' };
      if (response.status === 400) return { success: false, error: 'Please fill in all fields (password min 8 characters)' };
      return { success: false, error: 'Registration failed' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const loginWithCredentials = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setIsAuthenticated(true);
        track('signed_in', { provider: data.user?.provider || 'credentials', method: 'credentials' });
        return { success: true };
      }
      if (response.status === 401) return { success: false, error: 'Invalid email or password' };
      return { success: false, error: 'Login failed' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Start demo session - creates a temporary demo user
  const startDemo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/demo`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setIsAuthenticated(true);
        track('signed_in', { provider: 'demo', method: 'demo' });
        return { success: true };
      } else if (response.status === 403) {
        return { success: false, error: 'Demo mode is not available' };
      } else {
        return { success: false, error: 'Failed to start demo' };
      }
    } catch (error) {
      console.error('Failed to start demo:', error);
      return { success: false, error: error.message };
    }
  };

  // Logout - revoke session and clear cookies
  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      // Clear user-specific localStorage
      localStorage.removeItem('selectedOrganizationId');
    }
  };

  // Refresh access token using refresh token
  const refreshToken = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        // Token refreshed successfully, reload user data
        await getCurrentUser();
        return true;
      }

      if (response.status === 401) {
        // Refresh token invalid/expired, logout user
        console.warn('Session expired, logging out');
        setUser(null);
        setIsAuthenticated(false);
        return false;
      }

      // Other error, log but don't logout (might be network issue)
      console.error('Token refresh failed with status:', response.status);
      return false;
    } catch (error) {
      console.error('Token refresh network error:', error);
      // Network error - don't logout, user might be offline
      return false;
    }
  };

  // Wrapper for authenticated API calls
  // Automatically includes credentials (cookies) and handles token refresh with retry logic
  // Note: Relative URLs starting with /api will be prefixed with API_URL
  const authenticatedFetch = async (url, options = {}, retries = 1) => {
    // Prefix relative API URLs with the backend URL
    const fullUrl = url.startsWith('/api') ? `${API_URL}${url}` : url;

    const makeRequest = async () => {
      // A FormData body must set its OWN Content-Type, because the boundary is generated with
      // the body and the server cannot parse the parts without it. Forcing application/json
      // here made every multipart upload unparseable.
      const isMultipart = typeof FormData !== 'undefined' && options.body instanceof FormData;

      const response = await fetch(fullUrl, {
        ...options,
        credentials: 'include', // Include cookies
        headers: isMultipart
          ? { ...options.headers }
          : { ...options.headers, 'Content-Type': 'application/json' }
      });
      return response;
    };

    try {
      let response = await makeRequest();

      // A 402 PLAN_LIMIT is the same conversation wherever it comes from ("you are on Free,
      // here is what Pro gives you"), so it is recognised once here and published on the
      // plan-limit channel rather than handled by each caller. The response is cloned, so the
      // caller still receives an unread body and behaves exactly as before.
      if (response.status === 402) {
        await reportPlanLimit(response);
      }

      // If 401, cookies might be expired - try to refresh
      if (response.status === 401) {
        const refreshed = await refreshToken();

        if (refreshed) {
          // Retry original request with new access token cookie
          response = await makeRequest();
        } else {
          // Refresh failed - user needs to login again
          throw new Error('Session expired');
        }
      }

      return response;
    } catch (error) {
      // Retry on network errors (if retries remaining)
      if (retries > 0 && error.name === 'TypeError') {
        console.log(`Network error, retrying (${retries} attempts left)...`);
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1s
        return authenticatedFetch(url, options, retries - 1);
      }

      console.error('Authenticated fetch failed:', error);
      throw error;
    }
  };

  // Derived demo mode state from user object
  const isDemoMode = user?.isDemo ?? false;
  const demoFlowLimit = user?.demoFlowLimit ?? null;
  const demoExpiresAt = user?.demoExpiresAt ? new Date(user.demoExpiresAt) : null;

  const value = {
    user,
    isAuthenticated,
    isLoading,
    isDemoMode,
    demoFlowLimit,
    demoExpiresAt,
    loginWithGoogle,
    loginWithCredentials,
    register,
    startDemo,
    logout,
    getCurrentUser,
    authenticatedFetch
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
